import { useState, useContext } from 'react';
import { CircularProgress, IconButton, Tooltip, Button } from '@mui/material';
import { Edit, Add } from '@mui/icons-material';
import MDBox from 'src/components/md/MDBox';
import useOrganizationMembers from 'src/api/organization/useOrganizationMembers';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import DataTable from 'src/md-examples/Tables/DataTable';
import { Column } from 'react-table';
import { OrganizationMemberDTO } from 'src/api/organization/useOrganizationMembers';
import { AuthContext } from 'src/context/auth.context';
import { isAdmin } from 'src/utils/user';
import { useViewMode } from 'src/context/view-mode.context';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';

const MembersCard: React.FC = () => {
  const { data, isLoading, error, refetch } = useOrganizationMembers();
  const { currentUser } = useContext(AuthContext);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] =
    useState<OrganizationMemberDTO | null>(null);

  const { viewMode } = useViewMode();
  const isGentlyUser = isAdmin(currentUser, viewMode);
  const currentUserEmail = currentUser?.emails[0];

  const canEditUser = (member: OrganizationMemberDTO) => {
    return isGentlyUser || member.email === currentUserEmail;
  };

  const handleEditUser = (member: OrganizationMemberDTO) => {
    setSelectedMember(member);
  };

  const handleCloseEditModal = () => {
    setSelectedMember(null);
  };

  const handleSuccess = () => {
    refetch();
  };

  const baseColumns: Column<OrganizationMemberDTO>[] = [
    { Header: 'Name', accessor: 'name' },
    { Header: 'Email', accessor: 'email' },
    {
      Header: 'Groups',
      accessor: 'groups',
      Cell: ({ value }: { value: { groupId: string; name: string }[] }) =>
        value.length ? (
          <>{value.map((group) => group.name).join(', ')}</>
        ) : (
          <i style={{ color: 'lightgray' }}>None</i>
        ),
    },
    {
      Header: 'Admin',
      accessor: 'isAdmin',
      Cell: ({ value }: { value: boolean }) =>
        value ? (
          <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>Yes</span>
        ) : (
          <span style={{ color: '#999' }}>No</span>
        ),
    },
  ];

  const adminColumns: Column<OrganizationMemberDTO>[] = isGentlyUser
    ? [
        {
          Header: 'Status',
          accessor: 'deactivatedAt',
          Cell: ({ value }: { value: string | null }) => {
            if (value) {
              const deactivatedDate = new Date(value);
              return (
                <Tooltip
                  title={`Deactivated on ${deactivatedDate.toLocaleString()}`}
                >
                  <span style={{ color: '#f44336', fontWeight: 'bold' }}>
                    Deactivated
                  </span>
                </Tooltip>
              );
            }
            return (
              <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                Active
              </span>
            );
          },
        },
      ]
    : [];

  const actionColumn: Column<OrganizationMemberDTO> = {
    Header: 'Actions',
    accessor: 'userId',
    Cell: ({ row }: { row: { original: OrganizationMemberDTO } }) => {
      const member = row.original;
      return canEditUser(member) ? (
        <Tooltip title="Edit User">
          <IconButton
            size="small"
            onClick={() => handleEditUser(member)}
            color="primary"
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null;
    },
  };

  const columns: Column<OrganizationMemberDTO>[] = [
    ...baseColumns,
    ...adminColumns,
    actionColumn,
  ];

  if (isLoading) {
    return (
      <MDBox pt={2} pb={3} px={3} display="flex" justifyContent="center">
        <CircularProgress />
      </MDBox>
    );
  } else if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <MDBox pt={2} pb={3} px={3}>
      {isGentlyUser && (
        <MDBox mb={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsAddUserModalOpen(true)}
            color="primary"
          >
            Add User
          </Button>
        </MDBox>
      )}

      <DataTable<OrganizationMemberDTO>
        canSearch={false}
        table={{
          columns,
          rows: data?.members || [],
        }}
        pagination={{ variant: 'gradient', color: 'info' }}
        showTotalEntries={false}
        isSorted={false}
      />

      <AddUserModal
        open={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={handleSuccess}
      />

      <EditUserModal
        open={!!selectedMember}
        user={selectedMember}
        onClose={handleCloseEditModal}
        onSuccess={handleSuccess}
      />
    </MDBox>
  );
};

export default MembersCard;
